'use client';

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { faLockKeyhole } from '@fortawesome/pro-duotone-svg-icons';

interface ButtonProps {
  label?: string;
  onClick?: () => void;
  icon: IconDefinition;
  disabled?: boolean;
  locked?: boolean;
  isLoading?: boolean;
  buttonClassName?: string;
  titleClassName?: string;
  variant?: 'primary' | 'secondary';
  type?: 'button' | 'submit' | 'reset';
  iconClassName?: string;
}

const Button: React.FC<ButtonProps> = ({
  label,
  icon,
  disabled = false,
  locked = false,
  isLoading = false,
  onClick,
  variant = 'primary',
  type = 'button',
  buttonClassName,
  titleClassName,
  iconClassName,
}) => {
  const commonClasses = `flex items-center gap-2 text-center`;
  const isPrimary = variant === 'primary';

  const buttonStyles = isPrimary
    ? `btn btn-outline border-dark-gray-blue/70 bg-base-100 text-base font-medium text-primary hover:border-dark-gray-blue/70 hover:bg-base-100 hover:text-primary hover:underline max-xxs:w-full max-xxs:px-6`
    : `btn btn-primary rounded-lg bg-primary text-sm font-normal`;

  const textColor = isPrimary ? '' : 'text-primary-content';

  const disabledStyles = disabled || isLoading ? 'cursor-not-allowed opacity-50 pointer-events-none' : '';

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) {
      return;
    }

    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      className={`${buttonClassName} ${commonClasses} ${buttonStyles} ${disabledStyles}`}
      disabled={disabled}
    >
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        {isLoading ? (
          <span className="loading loading-spinner h-4 w-4"></span>
        ) : (
          <span className="relative">
            <FontAwesomeIcon icon={icon} className={`fa-fw h-4 w-4 ${iconClassName ?? ''}`} />
            {locked && (
              <FontAwesomeIcon icon={faLockKeyhole} className="fa-fw absolute -right-1.5 -top-1 h-3 w-3 text-accent" />
            )}
          </span>
        )}
        {label && <span className={`${titleClassName} ${textColor}`}>{label}</span>}
      </span>
    </button>
  );
};

export default Button;
